import { useQuery } from "@tanstack/react-query";

import { API_URL } from "@/constants/envVariables";
import { ACTIVE_ENROLLMENT_STATUSES } from "@/constants/multisigEnrollment";

import { EnrollmentError, mapEnrollmentError } from "@/helpers/enrollmentErrors";
import { fetchApi } from "@/helpers/fetchApi";

import type { AppError, MultisigEnrollmentSession, MultisigEnrollmentSessionStatus } from "@/types";

export const MULTISIG_ENROLLMENT_QUERY_KEY = ["multisigEnrollment"] as const;
export const MULTISIG_ENROLLMENT_URL = `${API_URL}/multisig/enrollment`;

/**
 * Whether the session is still in a status that could progress on the
 * backend. Used by `deriveCoSigningView` to route the UI between the
 * empty/intro state and the wizard — it no longer drives any auto-refetch.
 */
export const isActiveEnrollmentStatus = (
  status: MultisigEnrollmentSessionStatus | undefined | null,
): boolean => !!status && ACTIVE_ENROLLMENT_STATUSES.includes(status);

/**
 * `GET /multisig/enrollment`. A 404 means the organization has no active
 * session (none was started, or it reached a terminal status), so it resolves
 * to `null` — the empty view — rather than an error.
 */
export const fetchMultisigEnrollment = async (): Promise<MultisigEnrollmentSession | null> => {
  try {
    const session = (await fetchApi(MULTISIG_ENROLLMENT_URL)) as
      | MultisigEnrollmentSession
      | undefined;
    // `fetchApi` resolves `undefined` once it has fired the session-expired
    // event. That is a sign-in problem, not "no enrollment": returning `null`
    // here would render the empty view as if setup had never started.
    if (session === undefined) {
      throw mapEnrollmentError({ message: "", status: 401 });
    }
    return session;
  } catch (err) {
    if ((err as AppError)?.status === 404) return null;
    throw mapEnrollmentError(err);
  }
};

/**
 * `GET /multisig/enrollment` as a plain, one-shot query.
 *
 * No polling, no interval, no auto-refetch: the session is refreshed only
 * when one of the enrollment mutations invalidates
 * `MULTISIG_ENROLLMENT_QUERY_KEY` on success, or when a caller explicitly
 * calls `refetch()`.
 */
export const useMultisigEnrollment = (options?: { enabled?: boolean }) =>
  useQuery<MultisigEnrollmentSession | null, EnrollmentError>({
    queryKey: MULTISIG_ENROLLMENT_QUERY_KEY,
    queryFn: fetchMultisigEnrollment,
    enabled: options?.enabled ?? true,
  });
